import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useLocation, useHistory } from 'react-router-dom';
import * as AppPics from 'assets/images';
import usePaginateDefaults from 'hooks/usePaginateDefaults';
import TablePaginate from 'components/Paginate';
import usePlayables from 'hooks/API/GET/usePlayables';
import useDeletePlayable from 'hooks/API/DELETE/useDeletePlayable';
import BasicDelete from 'components/molecules/Modal/Delete/BasicDelete';
// import { AppModal } from 'components/molecules/Modal/AppModal';
import { useQueryClient } from 'react-query';
import { notifier } from 'components/Toaster';
import useDebounce from 'hooks/useDebounce';
import { AppSwitchBox } from 'components';
import useQueryParams from 'hooks/useQueryParams';
import MetaInfoPopup from './metaInfo';
import AppBreadcrumbs from 'components/molecules/AppBreadcrumbs';
// import DetailsHeader from 'components/molecules/DetailsHeader';
import { ArrowsPng } from 'assets/images';

function PlayableList() {
  const query = useQueryParams();
  const { podcastId, episodeId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const redirectBack = searchParams.get('redirect_back');

  const urlParams = new URLSearchParams(location.search);
  const initialKeyword = urlParams.get('keyword') || '';
  const initialFeatured = urlParams.get('featured') || '';
  const initialStatus = urlParams.get('status') || '';
  const initialPage = parseInt(urlParams.get('page')) || 0;

  const [params, setParams] = usePaginateDefaults({ limit: 5, page: initialPage });
  const [search, handleSearch] = useState(initialKeyword);
  const [searchTerm, setSearchTerm] = useState(initialKeyword);
  const [filterFeatured, setFilterFeatured] = useState(initialFeatured);
  const [enableFilter, setEnableFilter] = useState(false);
  const [filterStatus, setFilterStatus] = useState(initialStatus);

  const [metaPopup, setMetaPopup] = useState({
    open: false,
    data: null,
  });

  useEffect(() => {
    const updatedParams = new URLSearchParams(location.search);
    const newKeyword = updatedParams.get('keyword') || '';
    const newFeatured = updatedParams.get('featured') || '';
    const newStatus = updatedParams.get('status') || '';
    setFilterStatus(newStatus);

    const newPage = parseInt(updatedParams.get('page')) || 0;

    setSearchTerm(newKeyword);
    handleSearch(newKeyword);
    setFilterFeatured(newFeatured);
    setParams((prev) => ({ ...prev, page: newPage }));
  }, [location.key]);

  const searchKeyword = useDebounce(search, 1000);

  const updateURL = (newParams) => {
    const currentParams = new URLSearchParams(location.search);

    Object.entries(newParams).forEach(([key, value]) => {
      if (value && typeof value === 'string' && value.trim() !== '') {
        currentParams.set(key, value);
      } else {
        currentParams.delete(key);
      }
    });

    const newSearch = currentParams.toString();
    const newURL = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;

    if (newURL !== location.pathname + location.search) {
      history.replace(newURL);
    }
  };

  useEffect(() => {
    updateURL({
      keyword: searchKeyword,
      featured: filterFeatured,
      status: filterStatus,
      page: params.page,
    });
  }, [searchKeyword, filterFeatured, filterStatus, params.page]);

  const playableParams = useMemo(() => {
    return {
      ...params,
      keyword: searchKeyword,
      ...(filterFeatured === 'featured' && { featured: true }),
      ...(filterFeatured === 'not_featured' && { featured: false }),
      ...(filterStatus === 'errored' && { status: 'errored' }),
    };
  }, [params, searchKeyword, filterFeatured]);

  const { data, isLoading, refetch } = usePlayables({
    podcastId,
    episodeId,
    enabled: !!episodeId,
    configs: {
      params: playableParams,
    },
  });

  const { meta: { page, total } = {} } = data ?? {};
  const queryClient = useQueryClient();
  const playables = data?.result || [];

  const episodeName = useMemo(() => {
    if (playables.length > 0 && playables[0].podcastEpisode?.name) {
      return playables[0].podcastEpisode.name;
    }
    return data?.meta?.paths?.episodePath || null;
  }, [playables, data]);

  const podcastName = useMemo(() => {
    if (playables.length > 0 && playables[0].podcastEpisode?.podcast?.name) {
      return playables[0].podcastEpisode.podcast.name;
    }
    return data?.meta?.paths?.podcastPath || null;
  }, [playables, data]);

  const getBreadcrumbLinks = () => {
    const baseLinks = [
      { label: 'Dashboard', to: '/dashboard' },
      { label: 'Podcast Home', to: '/podcast' },
      { label: 'Podcasts', to: '/podcast/list' },
    ];

    // Add podcast name if available
    if (podcastName) {
      baseLinks.push({
        label: podcastName,
        to: `/podcast/${podcastId}/episodes`,
      });
    }

    baseLinks.push({
      label: 'Episodes',
      to: `/podcast/${podcastId}/episodes`,
    });

    // Add episode name if available
    if (episodeName) {
      baseLinks.push({
        label: episodeName,
        to: `/podcast/${podcastId}/episode/${episodeId}/playables`,
      });
    }

    return baseLinks;
  };

  const actionsConfig = {
    open: false,
    data: {},
    Header: null,
    Body: null,
    isDeleteOperation: false,
  };
  const [actions, setActions] = useState({ ...actionsConfig });

  const { mutate: deletePlayable } = useDeletePlayable();

  const handleDelete = (playable) => {
    setActions({
      open: true,
      data: { id: playable._id },
      Header: 'Delete Play file',
      Body: `Are you sure you want to delete "${playable.name}"?`,
      isDeleteOperation: true,
    });
  };

  const handleDeleteConfirm = () => {
    if (!actions?.data?.id) return;
    deletePlayable(actions.data.id, {
      onSuccess: (res) => {
        const message = res?.data?.data?.message || res?.data?.message;
        notifier.success(message);
        queryClient.invalidateQueries(['playables', podcastId, episodeId]);
        refetch();
        setActions({ ...actionsConfig });
      },
      onError: (err) => {
        const message =
          err?.response?.data?.message || err?.message || 'Failed to delete play file';
        notifier.error(message);
        setActions({ ...actionsConfig });
      },
    });
  };

  const performSearch = (value) => {
    handleSearch(value);
    setSearchTerm(value);
  };

  const clearAllFilters = () => {
    setFilterFeatured('');
    setFilterStatus('');
    performSearch('');
    setParams((prev) => ({ ...prev, page: 0 }));
    history.replace(location.pathname);
  };

  const handleBackNavigation = () => {
    if (redirectBack) {
      history.replace(decodeURIComponent(redirectBack));
    } else {
      // Navigate to episodes instead of using goBack()
      history.replace(`/podcast/${podcastId}/episodes`);
    }
  };

  const capitalize = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const handleViewMetadata = (metaInfo) => {
    setMetaPopup({
      open: true,
      data: metaInfo,
    });
  };

  return (
    <>
      <BasicDelete
        open={actions.open}
        closeModal={() => setActions({ ...actionsConfig })}
        Header={actions.Header}
        Body={actions.Body}
        handleConfirmation={handleDeleteConfirm}
      />
      <div className="mainWraper coursetabsection">
        <div className="tab">
          <ul className="tab__menu">
            <li className="back">
              <button className="btn-back" onClick={handleBackNavigation}>
                <img src={AppPics.BackSvg} alt="Back" />
              </button>
            </li>
          </ul>

          <div
            className="tab__content
          ___adjust___tab___padding"
          >
            <div className="tab__content-actHead">
              <AppBreadcrumbs links={getBreadcrumbLinks()} current="Play file" />
            </div>
            <div className="tab__content-actBody">
              <div className="px-5 py-5">
                <div className="tab__contentMenu">
                  <ul className="tab__contentMenu-list">
                    <li>
                      <h2 className="prime-head p-0 boldtitle text-nowrap">Manage Play File</h2>
                    </li>
                  </ul>

                  <div className="mainHeader__search">
                    <ul className="tab__contentMenu-list">
                      <li className="relative">
                        <input
                          className="mainHeader__searchInput"
                          placeholder="Search play files..."
                          type="text"
                          value={searchTerm}
                          onChange={(event) => {
                            const value = event.target.value;
                            setSearchTerm(value);
                            if (value === '') {
                              performSearch('');
                            }
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              performSearch(searchTerm);
                            }
                          }}
                        />
                        <span
                          className="mainHeader__searchBtn"
                          onClick={() => {
                            performSearch(searchTerm);
                          }}
                        >
                          <img src={AppPics.TopMenuSearchSvg} alt="Search" />
                        </span>
                      </li>
                      <li className="px-4 flex items-center">
                        <button
                          type="button"
                          onClick={() => setEnableFilter(!enableFilter)}
                          className="flex items-center gap-1"
                        >
                          <img src={AppPics.TabFilterSvg} alt="List filter" />
                          <span className="viewsettingtitle">Filter</span>
                        </button>
                      </li>
                    </ul>
                  </div>

                  <div className="flex items-center gap-10">
                    {!enableFilter && !filterFeatured && !filterStatus && (
                      <Link
                        to={`/podcast/${podcastId}/episodes/${episodeId}/playables/reorder`}
                        onClick={(e) => {
                          e.preventDefault();
                          history.replace(
                            `/podcast/${podcastId}/episodes/${episodeId}/playables/reorder`
                          );
                        }}
                        className="mr-4"
                      >
                        <img
                          src={ArrowsPng}
                          alt="Reorder Playables"
                          title="Reorder Playables"
                          className="cursor-pointer mr-6"
                        />
                      </Link>
                    )}

                    <Link to={`/podcast/${podcastId}/episode/${episodeId}/playable/add`}>
                      <button className="btn-left-content add">New Play File</button>
                    </Link>
                  </div>
                </div>
                <div className="tab__content-actBody">
                  {enableFilter && (
                    <div className="row mt-3">
                      <div className="col-lg-3">
                        <label className="label d-block">Filter Play file</label>
                        <div className="radio-group">
                          <AppSwitchBox
                            inputProps={{
                              checked: filterFeatured === 'featured',
                              onChange: (e) => {
                                const isChecked = e.target.checked;
                                setParams((prev) => ({ ...prev, page: 0 }));
                                if (isChecked) {
                                  setFilterFeatured('featured');
                                  query.set('featured', 'true');
                                } else {
                                  setFilterFeatured('');
                                  query.delete('featured');
                                }
                              },
                            }}
                            labelProps={{ label: 'Show Featured Only' }}
                          />
                        </div>
                      </div>
                      <div className="col-lg-3">
                        <label className="label d-block">Filter Status</label>
                        <div className="radio-group">
                          <AppSwitchBox
                            inputProps={{
                              checked: filterStatus === 'errored',
                              onChange: (e) => {
                                const isChecked = e.target.checked;
                                setParams((prev) => ({ ...prev, page: 0 }));
                                if (isChecked) {
                                  setFilterStatus('errored');
                                  query.set('status', 'errored');
                                } else {
                                  setFilterStatus('');
                                  query.delete('status');
                                }
                              },
                            }}
                            labelProps={{ label: 'Show Errored' }}
                          />
                        </div>
                      </div>

                      <div className="col-lg-3 d-flex">
                        <button
                          type="button"
                          className="btn btn-plane p--tint"
                          style={{ alignSelf: 'end' }}
                          onClick={clearAllFilters}
                        >
                          Clear filter
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="tab__contentBody">
                    <div className="tableWrap">
                      <div className="table-responsive">
                        <table className="table">
                          <thead className="thead-light __appTheadColor">
                            <tr>
                              <th className="align-center">#</th>
                              <th className="align-center">Play file Name</th>
                              <th className="align-center">Subtitle</th>
                              <th className="align-center">Artist</th>
                              <th className="align-center">Duration</th>
                              <th className="align-center">File Upload Status</th>
                              <th className="align-center">Status</th>
                              <th className="align-center">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {isLoading ? (
                              <tr>
                                <td colSpan={7} className="text-center py-4">
                                  Loading Play file...
                                </td>
                              </tr>
                            ) : playables.length > 0 ? (
                              playables.map((p, idx) => (
                                <tr key={p._id}>
                                  <td className="align-center">
                                    {params.page * params.limit + idx + 1}
                                  </td>
                                  <td className="align-center">{p.name || 'N/A'}</td>
                                  <td className="align-center w-25">{p.subtitle || 'N/A'}</td>
                                  <td className="align-center">{p.artists || 'N/A'}</td>
                                  <td className="align-center">
                                    {p.duration ? p.duration : 'N/A'}
                                  </td>
                                  <td className="align-center">
                                    {capitalize(p.status) || 'N/A'}
                                    {p.status === 'errored' &&
                                      !!p.metaInfo &&
                                      Object.keys(p.metaInfo || {}).length > 0 && (
                                        <button
                                          className="btn btn-sm btn-outline-primary ml-2"
                                          onClick={() => handleViewMetadata(p.metaInfo)}
                                        >
                                          View Meta
                                        </button>
                                      )}
                                  </td>
                                  <td className="text-center">
                                    {p?.isPublished ? 'Published' : 'Not Published'}
                                  </td>
                                  <td className="align-center">
                                    <Link
                                      className="actions-icons mx-2 text-primary"
                                      title="Edit"
                                      to={`/podcast/${podcastId}/episode/${episodeId}/playable/${
                                        p._id
                                      }?redirect_back=${encodeURIComponent(
                                        location.pathname + location.search
                                      )}`}
                                    >
                                      <img src={AppPics.EditSvg} alt="Edit" />
                                    </Link>
                                    <button
                                      className="actions-icons mx-2 text-danger"
                                      title="Delete"
                                      onClick={() => handleDelete(p)}
                                    >
                                      <img src={AppPics.DeleteSvg} alt="Delete" />
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : searchKeyword.trim() !== '' ? (
                              <tr>
                                <td colSpan={7} className="text-center py-4">
                                  No play file found for "{searchKeyword}"
                                </td>
                              </tr>
                            ) : (
                              <tr>
                                <td colSpan={7} className="text-center py-4">
                                  No play file available.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <MetaInfoPopup
                      isOpen={metaPopup.open}
                      data={metaPopup.data}
                      closeModal={() => setMetaPopup({ open: false, data: null })}
                    />

                    <TablePaginate
                      currentPage={page}
                      maxCount={total}
                      defaultLimit={params.limit}
                      setPage={(page) => setParams((prev) => ({ ...prev, page }))}
                      handleChange={(limit) => setParams((prev) => ({ ...prev, page: 0, limit }))}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PlayableList;
